import { CdkDrag, CdkDropList, DragDropRegistry } from "@angular/cdk/drag-drop";
import {
  AfterContentInit,
  ContentChildren,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
} from "@angular/core";
import { Subscription } from "rxjs";

@Directive({
  selector: "[appCdkDropListAutoScroll]",
  standalone: false,
})
export class CdkDropListAutoScrollDirective implements AfterContentInit, OnDestroy {
  private static readonly EDGE_ZONE_PX = 80;
  private static readonly MAX_SCROLL_SPEED = 15;

  @ContentChildren(CdkDrag, { descendants: true }) private dragItems: QueryList<CdkDrag>;

  private scrollElements: HTMLElement[] = [];
  private autoScrollAnimationId: number | null = null;
  private autoScrollSpeed = 0;
  private isDragging = false;
  private dragSubscriptions: Subscription[] = [];
  private queryListSubscription: Subscription | null = null;
  private beforeStartedSubscription: Subscription | null = null;
  private cleanupFns: (() => void)[] = [];
  private scrollElementsResolved = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private dropList: CdkDropList,
    private dragDropRegistry: DragDropRegistry,
  ) {}

  ngAfterContentInit(): void {
    this.resolveAndCacheScrollElements();
    this.subscribeToBeforeStarted();
    this.subscribeToDragItems();
    this.queryListSubscription = this.dragItems.changes.subscribe(() => {
      this.unsubscribeFromDragItems();
      this.subscribeToDragItems();
    });
  }

  ngOnDestroy(): void {
    this.queryListSubscription?.unsubscribe();
    this.beforeStartedSubscription?.unsubscribe();
    this.unsubscribeFromDragItems();
    this.stopAutoScroll();
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  /**
   * Subscribes to DropListRef.beforeStarted to inject our scroll elements
   * AFTER the CDK resolves its own scrollable parents. The CDK's CdkDropList
   * calls withScrollableParents() inside a beforeStarted handler, which would
   * overwrite any elements we registered earlier. By subscribing here, our
   * handler runs after the CDK's (since we subscribe later), so we can
   * append our elements to the list the CDK just set.
   */
  private subscribeToBeforeStarted(): void {
    const dropListRef = (this.dropList as any)._dropListRef;
    if (!dropListRef) {
      return;
    }
    this.beforeStartedSubscription = dropListRef.beforeStarted.subscribe(() => {
      this.injectScrollElements();
    });
  }

  /**
   * Appends the resolved shadow DOM scroll elements to the DropListRef's
   * scrollable parents list. Called on every beforeStarted, after the CDK's
   * own handler has already set its scrollable parents.
   */
  private injectScrollElements(): void {
    if (this.scrollElements.length === 0) {
      return;
    }
    const dropListRef = (this.dropList as any)._dropListRef;
    if (!dropListRef) {
      return;
    }
    const existing: HTMLElement[] = dropListRef.getScrollableParents();
    const combined = [...existing];
    let changed = false;
    for (const el of this.scrollElements) {
      if (!combined.includes(el)) {
        combined.push(el);
        changed = true;
      }
    }
    if (changed) {
      dropListRef.withScrollableParents(combined);
    }
  }

  private subscribeToDragItems(): void {
    this.dragItems.forEach((drag) => {
      this.dragSubscriptions.push(
        drag.started.subscribe(() => this.onDragStarted()),
        drag.moved.subscribe((event) => this.onDragMoved(event.pointerPosition.y)),
        drag.ended.subscribe(() => this.onDragEnded()),
      );
    });
  }

  private unsubscribeFromDragItems(): void {
    this.dragSubscriptions.forEach((sub) => sub.unsubscribe());
    this.dragSubscriptions = [];
  }

  private onDragStarted(): void {
    this.isDragging = true;
  }

  private onDragMoved(pointerY: number): void {
    const scrollElement = this.getVerticalScrollElement();
    if (!scrollElement) {
      return;
    }

    const rect = scrollElement.getBoundingClientRect();
    const distanceFromTop = pointerY - rect.top;
    const distanceFromBottom = rect.bottom - pointerY;
    const edgeZone = CdkDropListAutoScrollDirective.EDGE_ZONE_PX;
    const maxSpeed = CdkDropListAutoScrollDirective.MAX_SCROLL_SPEED;

    if (distanceFromTop < edgeZone && distanceFromTop >= 0) {
      const ratio = 1 - distanceFromTop / edgeZone;
      this.autoScrollSpeed = -Math.ceil(ratio * maxSpeed);
      this.startAutoScroll();
    } else if (distanceFromBottom < edgeZone && distanceFromBottom >= 0) {
      const ratio = 1 - distanceFromBottom / edgeZone;
      this.autoScrollSpeed = Math.ceil(ratio * maxSpeed);
      this.startAutoScroll();
    } else {
      this.autoScrollSpeed = 0;
      this.stopAutoScroll();
    }
  }

  private onDragEnded(): void {
    this.isDragging = false;
    this.stopAutoScroll();
  }

  private startAutoScroll(): void {
    if (this.autoScrollAnimationId !== null) {
      return;
    }

    const scrollElement = this.getVerticalScrollElement();
    if (!scrollElement) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const scrollLoop = () => {
        if (!this.isDragging || this.autoScrollSpeed === 0) {
          this.autoScrollAnimationId = null;
          return;
        }
        scrollElement.scrollTop += this.autoScrollSpeed;
        this.autoScrollAnimationId = requestAnimationFrame(scrollLoop);
      };
      this.autoScrollAnimationId = requestAnimationFrame(scrollLoop);
    });
  }

  private stopAutoScroll(): void {
    if (this.autoScrollAnimationId !== null) {
      cancelAnimationFrame(this.autoScrollAnimationId);
      this.autoScrollAnimationId = null;
    }
    this.autoScrollSpeed = 0;
  }

  /**
   * Returns the primary vertical scroll container (the ion-content scroll element).
   * Falls back to the first scrollable ancestor if ion-content isn't found.
   */
  private getVerticalScrollElement(): HTMLElement | null {
    for (const el of this.scrollElements) {
      if (this.isInsideShadowDom(el) || el.scrollHeight > el.clientHeight) {
        return el;
      }
    }
    return this.scrollElements[0] ?? null;
  }

  /**
   * Eagerly resolves scroll elements and sets up shadow DOM event forwarding.
   * The actual registration with CDK is deferred to the beforeStarted handler.
   */
  private async resolveAndCacheScrollElements(): Promise<void> {
    this.scrollElements = await this.resolveScrollableAncestors();
    this.scrollElementsResolved = true;

    if (this.scrollElements.length === 0) {
      return;
    }

    // Forward scroll events from shadow DOM elements to CDK's registry.
    // The CDK's document-level capture listener cannot see scroll events
    // originating inside shadow DOM, so we bridge the gap manually.
    const registry = this.dragDropRegistry as any;
    for (const el of this.scrollElements) {
      if (this.isInsideShadowDom(el)) {
        const handler = (event: Event) => {
          registry.scroll?.next(event);
        };
        this.ngZone.runOutsideAngular(() => {
          el.addEventListener("scroll", handler, true);
        });
        this.cleanupFns.push(() => el.removeEventListener("scroll", handler, true));
      }
    }
  }

  /**
   * Walks up the DOM from the host element collecting all scrollable ancestors.
   * For ion-content elements, resolves the actual inner scroll element from shadow DOM.
   */
  private async resolveScrollableAncestors(): Promise<HTMLElement[]> {
    const scrollables: HTMLElement[] = [];
    let current: HTMLElement | null = this.el.nativeElement.parentElement;

    while (current) {
      if (current.tagName === "ION-CONTENT") {
        const ionScrollEl = await (current as any).getScrollElement();
        if (ionScrollEl) {
          scrollables.push(ionScrollEl);
        }
        break;
      }

      if (this.isScrollable(current)) {
        scrollables.push(current);
      }

      current = current.parentElement;
    }

    return scrollables;
  }

  private isScrollable(el: HTMLElement): boolean {
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const isScrollableOverflow = (value: string) => value === "auto" || value === "scroll";
    return (
      (isScrollableOverflow(overflowY) && el.scrollHeight > el.clientHeight) ||
      (isScrollableOverflow(overflowX) && el.scrollWidth > el.clientWidth)
    );
  }

  private isInsideShadowDom(el: HTMLElement): boolean {
    return el.getRootNode() instanceof ShadowRoot;
  }
}
